import React, { useState } from 'react';
import { Wallet, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import EtatVide from '../ui/EtatVide';
import Pagination from '../ui/Pagination';
import VisionneuseJustificatif from '../ui/VisionneuseJustificatif';
import { MovementSensBadge } from './CentralBadges';
import { formatMontant, formatDateTime, getMovementTypeLabel } from './nosDepensesUtils';

// Sous-section calquee sur les sous-sections de MaCaisse (border-2 + en-tete pastel + tableau).
const MouvementsSection = ({ mouvements, pagination, setPagination }) => {
  // La pièce qu'on regarde. Locale à la section : personne d'autre n'en a besoin.
  const [pieceConsultee, setPieceConsultee] = useState(null);

  return (
    <div className="border-2 border-[#319190]/25 rounded-lg bg-[#319190]/5">
      <div className="p-4 bg-[#319190]/10 border-b border-[#319190]/25">
        <h3 className="text-lg font-semibold text-[#246990] flex items-center gap-2">
          <Wallet size={20} />
          Registre de la caisse centrale
        </h3>
        <p className="text-sm text-[#319190] mt-1">
          Remises validées, apports, dotations et ajustements qui composent le solde central
        </p>
      </div>

      <div className="p-4">
        {mouvements.length === 0 ? (
          <EtatVide
            icone={Wallet}
            titre="Aucun mouvement central"
            description="Les remises validées, apports, dotations et ajustements apparaîtront ici."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#319190]/15 border-b border-[#319190]/25">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#246990] uppercase">Date &amp; Heure</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#246990] uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#246990] uppercase">Libellé</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-[#246990] uppercase">Montant</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#246990] uppercase">Bénéficiaire</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#246990] uppercase">Enregistré par</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-[#246990] uppercase">Justificatif</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#319190]/15">
                {mouvements.map((mouvement) => {
                  const isEntree = mouvement.sens === 'entree';
                  const createur = [mouvement.cree_par_prenom, mouvement.cree_par_nom].filter(Boolean).join(' ') || '-';
                  const beneficiaire = [mouvement.beneficiaire_prenom, mouvement.beneficiaire_nom].filter(Boolean).join(' ') || '-';

                  return (
                    <tr key={mouvement.id} className="hover:bg-[#319190]/10 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-900 font-medium">
                          {new Date(mouvement.date_mouvement).toLocaleDateString('fr-FR')}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(mouvement.date_mouvement).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1.5">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#319190]/15 text-[#246990] w-fit">
                            {getMovementTypeLabel(mouvement.type)}
                          </span>
                          <MovementSensBadge sens={mouvement.sens} />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">
                          {mouvement.libelle || getMovementTypeLabel(mouvement.type)}
                        </p>
                        {mouvement.description && (
                          <p className="text-xs text-gray-500 mt-1">{mouvement.description}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <p className={`text-sm font-bold ${isEntree ? 'text-emerald-600' : 'text-red-600'}`}>
                          {isEntree ? '+' : '-'}{formatMontant(mouvement.montant)}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-700">{beneficiaire}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-700">{createur}</p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {/* La pièce passe par une route authentifiée : un lien
                            nu ne porte pas le jeton, et l'URL racine-relative
                            se résolvait contre l'origine du frontend — en
                            développement elle ouvrait la coquille de l'app. */}
                        {mouvement.justificatif_fichier_url ? (
                          <button
                            type="button"
                            onClick={() => setPieceConsultee({
                              source: 'mouvement',
                              id: mouvement.id,
                              titre: mouvement.justificatif_fichier_nom || 'Justificatif',
                              sousTitre: getMovementTypeLabel(mouvement.type)
                            })}
                            className="inline-flex p-2 text-[#319190] hover:bg-[#319190]/15 rounded-lg transition-colors"
                            title="Ouvrir le justificatif"
                          >
                            <FileText size={18} />
                          </button>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 border-t pt-4">
          <Pagination
            page={pagination.page}
            nombrePages={pagination.total_pages}
            total={pagination.total}
            parPage={pagination.limit}
            onChangerPage={(page) => setPagination((prev) => ({ ...prev, page }))}
            libelleElement="mouvement"
          />
        </div>
      </div>

      <VisionneuseJustificatif
        ouvert={Boolean(pieceConsultee)}
        onFermer={() => setPieceConsultee(null)}
        source={pieceConsultee?.source}
        id={pieceConsultee?.id}
        titre={pieceConsultee?.titre}
        sousTitre={pieceConsultee?.sousTitre}
      />
    </div>
  );
};

export default MouvementsSection;
